import 'react-data-grid/lib/styles.css';
import { UNIT_CELL_FIELDS, type UnitCellField } from '@sivan/shared';
import { type ClipboardEvent, useMemo, useRef, useState } from 'react';
import { type Column, DataGrid, type RenderEditCellProps, type SortColumn } from 'react-data-grid';
import { type ClipboardTable, PasteTooLargeError, parseClipboardTable } from './clipboard';
import { FIELD_LABELS, OUTCOME_LABELS, ROW_STATE_LABELS, errorText } from './labels';
import { type GridRow, changedFields, rowState } from './pending';
import { rowNumberColumn } from './row-number-column';

/** The grid's view of a row: the pending row plus its cells flattened for react-data-grid. */
type ViewRow = { readonly row: GridRow } & Readonly<Record<UnitCellField, string>>;

/** Fixed widths; the model type column (undefined) takes the remaining width. */
const WIDTHS: Readonly<Record<UnitCellField, number | undefined>> = {
  code: 110,
  displayNumber: 110,
  kind: 100,
  building: 80,
  floor: 70,
  modelType: undefined,
  rooms: 80,
  areaM2: 100,
  status: 110,
};

interface UnitGridProps {
  readonly rows: readonly GridRow[];
  readonly canEditStored: boolean;
  readonly canCreate: boolean;
  readonly sortColumns: readonly SortColumn[];
  readonly onSortColumnsChange: (columns: SortColumn[]) => void;
  readonly onCellChange: (key: string, field: UnitCellField, value: string) => void;
  readonly onPaste: (
    table: ClipboardTable,
    anchorIndex: number,
    anchorField: UnitCellField,
  ) => void;
  readonly onPasteRejected: (message: string) => void;
}

function CellEditor({ row, column, onRowChange, onClose }: RenderEditCellProps<ViewRow>) {
  const field = column.key as UnitCellField;
  return (
    <input
      className="grid-editor"
      aria-label={FIELD_LABELS[field]}
      // The editor takes focus as soon as it opens, like a spreadsheet cell.
      autoFocus
      value={row[field]}
      onChange={(event) => {
        onRowChange({ ...row, [field]: event.target.value });
      }}
      onBlur={() => {
        onClose(true);
      }}
    />
  );
}

function StateCell({ row }: { readonly row: GridRow }) {
  const state = rowState(row);
  const outcome = row.outcome;
  const problem = outcome === 'invalid' || outcome === 'duplicate' || outcome === 'conflict';
  const hasErrors = Object.keys(row.errors).length > 0;
  const label = problem ? OUTCOME_LABELS[outcome] : hasErrors ? 'שגיאה' : ROW_STATE_LABELS[state];
  const tone = problem || hasErrors ? 'error' : state;
  return <span className={`chip chip--${tone}`}>{label}</span>;
}

/**
 * The Excel-like units grid. react-data-grid renders, navigates and edits cells (RTL); this file
 * adds the clipboard adapter: Ctrl/Cmd+V reads `text/plain` only, parses it as a table and hands
 * it to the pending model, which places it from the selected cell and grows rows as needed.
 */
export function UnitGrid(props: UnitGridProps) {
  const { rows, canEditStored, canCreate } = props;
  const anchor = useRef<{ rowIdx: number; field: UnitCellField }>({ rowIdx: 0, field: 'code' });
  const [activeRow, setActiveRow] = useState(-1);

  const viewRows = useMemo(() => rows.map((row): ViewRow => ({ row, ...row.cells })), [rows]);

  const columns = useMemo((): Column<ViewRow>[] => {
    const editable = (view: ViewRow) => (view.row.unitId === null ? canCreate : canEditStored);
    return [
      rowNumberColumn<ViewRow>(activeRow, (view) => {
        const state = rowState(view.row);
        const error = Object.keys(view.row.errors).length > 0;
        return {
          tone: error ? 'error' : state,
          label: error ? 'שגיאה' : state === 'stored' ? null : ROW_STATE_LABELS[state],
        };
      }),
      {
        key: 'state',
        name: 'מצב',
        width: 104,
        frozen: true,
        renderCell: ({ row }) => <StateCell row={row.row} />,
      },
      ...UNIT_CELL_FIELDS.map((field): Column<ViewRow> => ({
        key: field,
        name: FIELD_LABELS[field],
        ...(WIDTHS[field] === undefined ? { minWidth: 130 } : { width: WIDTHS[field] }),
        sortable: true,
        editable,
        renderEditCell: CellEditor,
        cellClass: (view) => {
          const classes = [];
          if (view.row.errors[field] !== undefined) classes.push('cell--error');
          else if (view.row.unitId !== null && changedFields(view.row).includes(field))
            classes.push('cell--changed');
          return classes.join(' ');
        },
        renderCell: ({ row: view }) => {
          const code = view.row.errors[field];
          return (
            <span title={code === undefined ? undefined : errorText(field, code)}>
              {view[field]}
              {code !== undefined && (
                <span className="visually-hidden"> ({errorText(field, code)})</span>
              )}
            </span>
          );
        },
      })),
    ];
  }, [activeRow, canCreate, canEditStored]);

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('input, textarea') !== null) return; // typing in a cell
    event.preventDefault();
    let table: ClipboardTable;
    try {
      table = parseClipboardTable(event.clipboardData.getData('text/plain'));
    } catch (error) {
      props.onPasteRejected(
        error instanceof PasteTooLargeError
          ? 'ההדבקה גדולה מדי (עד 1,000 שורות ו-20 עמודות).'
          : 'לא ניתן לקרוא את התוכן שהודבק.',
      );
      return;
    }
    if (table.length === 0) return;
    const { rowIdx, field } = anchor.current;
    const touchesStored = rows
      .slice(rowIdx, rowIdx + table.length)
      .some((row) => row.unitId !== null);
    const createsRows =
      rows.slice(rowIdx, rowIdx + table.length).filter((row) => row.unitId !== null).length <
      table.length;
    if ((touchesStored && !canEditStored) || (createsRows && !canCreate)) {
      props.onPasteRejected('אין לך הרשאה לבצע את השינויים האלה.');
      return;
    }
    props.onPaste(table, rowIdx, field);
  };

  return (
    <div className="grid-frame" onPaste={handlePaste}>
      <DataGrid
        aria-label="טבלת יחידות"
        className="units-grid"
        direction="rtl"
        columns={columns}
        rows={viewRows}
        rowKeyGetter={(view) => view.row.key}
        rowHeight={36}
        headerRowHeight={40}
        sortColumns={props.sortColumns}
        onSortColumnsChange={props.onSortColumnsChange}
        rowClass={(view) => `row--${rowState(view.row)}`}
        onActivePositionChange={({ rowIdx, column }) => {
          setActiveRow(rowIdx);
          // The row-number column is never a paste target: a paste there starts at the first field.
          const key = column?.key;
          const field = UNIT_CELL_FIELDS.find((f) => f === key) ?? 'code';
          anchor.current = { rowIdx: Math.max(0, rowIdx), field };
        }}
        onCellKeyDown={(_args, event) => {
          // Ctrl/Cmd+V pastes a range (handled above) instead of opening the cell editor.
          if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v')
            event.preventGridDefault();
        }}
        onRowsChange={(next, { indexes, column }) => {
          const field = column.key as UnitCellField;
          for (const index of indexes) {
            const view = next[index];
            if (view !== undefined && view[field] !== view.row.cells[field])
              props.onCellChange(view.row.key, field, view[field]);
          }
        }}
      />
    </div>
  );
}
